import type { CurrentUser } from '@zhiyue/core';
import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const REFRESH_BUFFER_MS = 30_000;
const FALLBACK_TTL_MS = 2 * 60_000;

export type OpenPlatformTokenResult = {
  accessToken: string;
  expiresAt: number;
};

type TokenContext = Pick<CurrentUser, 'tenantUuid' | 'memberUuid'> & {
  organizationUuid?: string;
};

const keyOf = (user?: TokenContext | null) =>
  [
    user?.tenantUuid || user?.organizationUuid,
    user?.organizationUuid,
    user?.memberUuid,
  ]
    .map((value) => String(value || '').trim())
    .join('|');

let activeKey = '';
let generation = 0;
let cachedToken: OpenPlatformTokenResult | undefined;
let pending: Promise<OpenPlatformTokenResult> | undefined;

export const clearOpenPlatformToken = () => {
  generation += 1;
  activeKey = '';
  cachedToken = undefined;
  pending = undefined;
};

const readTokenResponse = (response: unknown): OpenPlatformTokenResult => {
  const result = response as {
    data?: { accessToken?: unknown; expiresAt?: unknown };
    accessToken?: unknown;
    expiresAt?: unknown;
  };
  const value = result?.data || result;
  if (typeof value.accessToken !== 'string' || !value.accessToken.trim()) {
    throw new Error('开放平台 Token 响应无效');
  }

  // BFF 返回上游 JWT 的 exp（毫秒）；旧响应缺少 exp 时仅短暂缓存。
  const expiresAt =
    typeof value.expiresAt === 'number' && Number.isFinite(value.expiresAt)
      ? value.expiresAt
      : Date.now() + FALLBACK_TTL_MS;
  if (expiresAt <= Date.now() + REFRESH_BUFFER_MS) {
    throw new Error('开放平台 Token 响应无效');
  }
  return { accessToken: value.accessToken, expiresAt };
};

export async function fetchOpenPlatformToken(
  user: TokenContext,
  rejectedToken?: string,
): Promise<OpenPlatformTokenResult> {
  const key = keyOf(user);
  if (!key.split('|').every(Boolean)) {
    throw new Error('当前会话缺少开放平台认证信息');
  }

  if (activeKey !== key) {
    generation += 1;
    activeKey = key;
    cachedToken = undefined;
    pending = undefined;
  }

  if (
    cachedToken &&
    cachedToken.expiresAt > Date.now() + REFRESH_BUFFER_MS &&
    (!rejectedToken || cachedToken.accessToken !== rejectedToken)
  ) {
    return cachedToken;
  }
  if (pending) return pending;

  const version = generation;
  const promise = request<unknown>('/bff/open-platform/token', {
    method: 'GET',
    skipErrorHandler: true,
    ...(rejectedToken
      ? { headers: { 'x-rejected-m2m-token': rejectedToken } }
      : {}),
  })
    .then((response) => {
      const nextToken = readTokenResponse(response);
      if (version !== generation || key !== activeKey) {
        throw new Error('会话已切换');
      }
      cachedToken = nextToken;
      return nextToken;
    })
    .finally(() => {
      if (pending === promise) pending = undefined;
    });
  pending = promise;
  return promise;
}
